import * as Yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { Box, Button as MuiButton, Typography, FormControl, Grow } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import PasswordInput from './PasswordInput';
import { passwordRegex } from '@buildwise/libs/signup/constants';
import { styled } from '@mui/material/styles';

export type ResetPasswordFormData = {
  firstname: string;
  lastname: string;
  email: string;
  phoneNumber: string;
  title: string;
  password: string;
  confirmPassword: string;
};

export const ResetPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .required('Required field')
    .min(6, 'Password must contain at least 6 characters')
    .max(20, 'Password must not be longer than 20 characters')
    .matches(
      passwordRegex,
      `Use a minimum length of 6 characters\n
      Use a maximum length of 20 characters\n
      Use a mix of alphabets and numbers\n
      Use at least one uppercase character\n`,
    )
    .nullable(),
  confirmPassword: Yup.string()
    .required('Required field')
    .oneOf([Yup.ref('password'), null], 'Password does not match'),
});
// `Use at least one uppercase letter (A-Z)\n
//       Use at least one lowercase letter (a-z)\n
//       Use at least one digit (0-9)\n
//       Use at least one special character from the set [@$!%*?&]\n
//       No sequential numbers (e.g., 012, 123, 234, etc.) are allowed
//       `,

const ResetPasswordForm = ({ onSubmit, loading }) => {
  const { handleSubmit, control } = useForm<ResetPasswordFormData>({
    mode: 'all',
    resolver: yupResolver(ResetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  return (
    <Grow in style={{ transformOrigin: 'top center' }}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Title>Enter your new password</Title>

        <Controller
          control={control}
          render={({ field, fieldState }) => (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <PasswordInput
                {...field}
                autoFocus
                fullWidth
                error={!!fieldState.error}
                helperText={
                  fieldState?.error?.message ? (
                    <>
                      {fieldState?.error?.message.split('/n').map((m) => (
                        <span style={{ display: 'block' }} key={m}>
                          {m}
                        </span>
                      ))}
                    </>
                  ) : (
                    ''
                  )
                }
                variant="outlined"
                placeholder="Password"
                autoComplete="new-password"
              />
            </FormControl>
          )}
          name="password"
        />

        <Controller
          control={control}
          render={({ field, fieldState }) => (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <PasswordInput
                {...field}
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState?.error?.message || ''}
                variant="outlined"
                placeholder="Re-type Password"
                autoComplete="new-password"
              />
            </FormControl>
          )}
          name="confirmPassword"
        />

        <Box display="flex" flexDirection="column" mb={2} mt={1}>
          <Button fullWidth variant="contained" disabled={loading} type="submit">
            {loading ? 'Saving...' : 'Submit'}
          </Button>
        </Box>
      </Form>
    </Grow>
  );
};

const Form = styled('form', { label: 'Form' })(({ theme }) => ({
  maxWidth: 500,
  width: '100%',
  padding: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.custom.black,
      },
    },
  },
}));

const Title = styled(Typography, { label: 'Title' })(({ theme }) => ({
  fontSize: 36,
  fontWeight: 700,
  color: theme.palette.custom.mirage,
  marginBottom: 24,
  [theme.breakpoints.down('md')]: {
    fontSize: 24,
  },
}));

const Button = styled(MuiButton, { label: 'Button' })({
  borderRadius: 8,
  height: 56,
});

export default ResetPasswordForm;
